import { ReactElement } from "react";

import {
  InteractionObjectType,
  ProjectIssuanceConstants,
  ProjectIssuanceTransactionConstants,
} from "../../../../constants";
import { GetProjectIssuancesResponse, SearchIssuanceTransactionsResponse } from "../../../../service/query";
import { formatNumber } from "../../../../utils";
import {
  ActionButtons,
  Column,
  DatePicker,
  ErrorBox,
  FlyoutPanel,
  FormLabel,
  PillsList,
  ReadonlyText,
  Row,
  Select,
  TextInput,
} from "../../../../widget";
import { isUnitKmHabitatType } from "../../../IssuancePanel";
import { TagsPanel } from "../../../TagsPanel";
import { ReleaseToInventoryModal } from "./components";
import { useAddOrLinkIssuanceTransactionPanel } from "./useAddOrLinkIssuanceTransactionPanel";

interface AddOrLinkIssuanceTransactionPanelProps {
  issuance?: GetProjectIssuancesResponse["results"][0];
  issuanceTransaction?: SearchIssuanceTransactionsResponse["results"][0]; // Parent transaction
  show: boolean;
  onClose: () => void;
  handleRefresh: () => void;
}

export const AddOrLinkIssuanceTransactionPanel = ({
  issuance,
  issuanceTransaction,
  show,
  onClose,
  handleRefresh,
}: AddOrLinkIssuanceTransactionPanelProps): JSX.Element => {
  const {
    errors,
    handleClosePanel,
    handleSubmit,
    isHandleSubmitLoading,
    issuanceTransactionPanelData,
    setIssuanceTransactionPanelData,
    showReleaseToInventoryModal,
    releaseToInventoryType,
    showTagsPanel,
    setShowTagsPanel,
    changedTagsExternal,
    setChangedTagsExternal,
    setRefreshTags,
    tagPills,
  } = useAddOrLinkIssuanceTransactionPanel({
    show,
    issuance,
    issuanceTransaction,
    onClose,
    handleRefresh,
  });

  const renderErrors = (): ReactElement | undefined => {
    return errors && errors.length > 0 ? (
      <Row spacingV="ll">
        <Column span={12}>
          <ErrorBox text={errors.map((error) => error.message)} />
        </Column>
      </Row>
    ) : undefined;
  };

  const content = (): ReactElement => {
    return (
      <form onSubmit={(e) => handleSubmit(e)}>
        {issuanceTransaction === undefined ? (
          <Row spacingV="xl">
            <Column span={12}>
              <p className="body1">
                If this transaction completes or cancels a previous reservation or sale, please settle it against the
                existing transaction in the table to keep the inventory accurate.
              </p>
            </Column>
          </Row>
        ) : (
          <Row spacingV="ll">
            <Column span={12}>
              <ReadonlyText label="Previous transaction ID" value={issuanceTransaction.uuid} />
            </Column>
          </Row>
        )}
        <Row spacingV="ll">
          <Column span={12}>
            {issuanceTransaction?.counterParty ? (
              <ReadonlyText label="Counterparty" value={issuanceTransactionPanelData.counterParty} />
            ) : (
              <TextInput
                label="Counterparty"
                name="Counterparty"
                value={issuanceTransactionPanelData.counterParty}
                onChange={(value) =>
                  setIssuanceTransactionPanelData({ ...issuanceTransactionPanelData, counterParty: value ?? null })
                }
              />
            )}
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TextInput
              label="Broker"
              name="Broker"
              value={issuanceTransactionPanelData.broker}
              onChange={(value) =>
                setIssuanceTransactionPanelData({ ...issuanceTransactionPanelData, broker: value ?? null })
              }
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <DatePicker
              label="Date"
              value={issuanceTransactionPanelData.transactionDate ?? undefined}
              onChange={(value) =>
                setIssuanceTransactionPanelData({ ...issuanceTransactionPanelData, transactionDate: value ?? null })
              }
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <Select
              label="Type"
              data={
                issuanceTransaction
                  ? ProjectIssuanceTransactionConstants.APPEND_ISSUANCE_TRANSACTION_TYPE_SELECT_DATA.filter(
                      (x) =>
                        (issuanceTransaction.type ===
                          ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_SOLD &&
                          x.key !== ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_SOLD) ||
                        issuanceTransaction.type !== ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_SOLD
                    )
                  : ProjectIssuanceTransactionConstants.ADD_ISSUANCE_TRANSACTION_TYPE_SELECT_DATA
              }
              value={issuanceTransactionPanelData.type}
              onChange={(value) => setIssuanceTransactionPanelData({ ...issuanceTransactionPanelData, type: value })}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TextInput
              label="Quantity"
              name="Quantity"
              type="number"
              value={issuanceTransactionPanelData.quantity?.toString()}
              onChange={(value) =>
                setIssuanceTransactionPanelData({
                  ...issuanceTransactionPanelData,
                  quantity: value === "" ? null : parseFloat(Number(value).toFixed(5)),
                  totalValue:
                    issuanceTransactionPanelData.price && value !== ""
                      ? parseFloat(Number(value).toFixed(5)) * issuanceTransactionPanelData.price -
                        (issuanceTransactionPanelData.fees ?? 0)
                      : null,
                })
              }
              minNumberValue={0}
              maxNumberValue={999999.99}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TextInput
              label="Price"
              name="Price"
              type="number"
              prefix="£"
              value={issuanceTransactionPanelData.price?.toString()}
              onChange={(value) =>
                setIssuanceTransactionPanelData({
                  ...issuanceTransactionPanelData,
                  price: value === "" ? null : parseFloat(Number(value).toFixed(4)),
                  totalValue:
                    issuanceTransactionPanelData.quantity && value !== ""
                      ? parseFloat(Number(value).toFixed(4)) * issuanceTransactionPanelData.quantity -
                        (issuanceTransactionPanelData.fees ?? 0)
                      : null,
                })
              }
              minNumberValue={0}
              maxNumberValue={9999999999.99999}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TextInput
              label="Fees"
              name="Fees"
              type="number"
              prefix="£"
              value={issuanceTransactionPanelData.fees?.toString()}
              onChange={(value) =>
                setIssuanceTransactionPanelData({
                  ...issuanceTransactionPanelData,
                  fees: value === "" ? null : parseFloat(Number(value).toFixed(4)),
                  totalValue:
                    issuanceTransactionPanelData.price && issuanceTransactionPanelData.quantity
                      ? issuanceTransactionPanelData.price * issuanceTransactionPanelData.quantity -
                        (value === "" ? 0 : parseFloat(Number(value).toFixed(4)))
                      : null,
                })
              }
              minNumberValue={0}
              maxNumberValue={9999999999.99999}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <ReadonlyText
              label="Total value"
              value={
                issuanceTransactionPanelData.totalValue !== null
                  ? `£${formatNumber(issuanceTransactionPanelData.totalValue)}`
                  : undefined
              }
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TextInput
              label="Transaction reference"
              name="Transaction reference"
              value={issuanceTransactionPanelData.externalReference}
              onChange={(value) =>
                setIssuanceTransactionPanelData({ ...issuanceTransactionPanelData, externalReference: value })
              }
            />
          </Column>
        </Row>
        {(issuance?.unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY ||
          issuanceTransaction?.unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY) && (
          <Row spacingV="ll">
            <Column span={12}>
              <TextInput
                label={`Unit size (${isUnitKmHabitatType(issuance?.habitatType ?? issuanceTransaction?.habitatGroup ?? null) ? "Km" : "Ha"})`}
                name="Unit size Ha / Km"
                type="number"
                value={issuanceTransactionPanelData.areaHa?.toString()}
                onChange={(value) =>
                  setIssuanceTransactionPanelData({
                    ...issuanceTransactionPanelData,
                    areaHa: value === "" ? null : Number(value),
                  })
                }
                minNumberValue={0}
              />
            </Column>
          </Row>
        )}
        <Row spacingV="ll">
          <Column span={12}>
            <FormLabel label="Tags" />
            <PillsList pills={tagPills} linkText="+ Add a tag" linkOnClick={() => setShowTagsPanel(true)} />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <ActionButtons
              submitButtonLabel="Save changes"
              onCancel={handleClosePanel}
              isLoading={isHandleSubmitLoading}
            />
          </Column>
        </Row>
      </form>
    );
  };

  return releaseToInventoryType && issuanceTransaction ? (
    <ReleaseToInventoryModal
      type={releaseToInventoryType}
      show={showReleaseToInventoryModal}
      onClose={() => handleClosePanel()}
      handleRefresh={handleRefresh}
      parentIssuanceTransaction={issuanceTransaction}
      issuanceTransactionPanelData={issuanceTransactionPanelData}
    />
  ) : (
    <FlyoutPanel
      show={show}
      onClose={handleClosePanel}
      size="ExtraSmall"
      contentChildren={
        <>
          {renderErrors()}
          {content()}
          <TagsPanel
            objectUuid={undefined}
            objectType={InteractionObjectType.ISSUANCE_TRANSACTION}
            show={showTagsPanel}
            onClose={() => setShowTagsPanel(false)}
            shouldRefreshTags={() => setRefreshTags((prev) => !prev)}
            changedTagsExternal={changedTagsExternal}
            setChangedTagsExternal={setChangedTagsExternal}
          />
        </>
      }
      header={`${issuanceTransaction === undefined ? "Add" : "Settle"} transaction`}
      buttonsChildren={undefined}
      height="Full"
    />
  );
};
